import type { IApiMediaWithCropsModel } from "#/_generated/umbraco/delivery-api/models";
import { getUmbracoCmsBaseUrlEnv } from "#/_packages/umbrella/config/utils.ts";
import type { ResponsiveImageAttributes } from "#/_packages/umbrella/types";
import { getAltText } from "#/_packages/umbrella/utils/image-utils.ts";

type MediaSource = Pick<
	IApiMediaWithCropsModel,
	"url" | "width" | "height" | "extension" | "crops" | "focalPoint"
>;
export type CmsImageOptions = {
	aspectRatio?: string | null;
	width?: number;
	height?: number;
	cropAlias?: string;
	sizes?: string;
	priority?: boolean;
};
const candidateWidths = [320, 480, 640, 768, 960, 1280, 1600, 1920, 2560];
const fallbackOrigin = "https://umbrella.invalid";
const positive = (value?: number | null) =>
	typeof value === "number" && Number.isFinite(value) && value >= 1
		? Math.floor(value)
		: undefined;

function cmsBase() {
	try {
		const base = new URL(getUmbracoCmsBaseUrlEnv() || fallbackOrigin);
		return /^https?:$/.test(base.protocol) ? base : new URL(fallbackOrigin);
	} catch {
		return new URL(fallbackOrigin);
	}
}

export function resolveMediaUrl(value?: string | null): string | undefined {
	if (!value) return undefined;
	try {
		const url = new URL(value, cmsBase());
		if (!["http:", "https:", "data:", "blob:"].includes(url.protocol))
			return undefined;
		return url.origin === fallbackOrigin
			? `${url.pathname}${url.search}${url.hash}`
			: url.toString();
	} catch {
		return undefined;
	}
}

function ratio(value?: string | null) {
	const normalized =
		value === "landscape"
			? "16:9"
			: value === "square"
				? "1:1"
				: value === "portrait"
					? "3:4"
					: value;
	const match = normalized?.match(/^(\d+(?:\.\d+)?)\s*[:-]\s*(\d+(?:\.\d+)?)$/);
	if (!match) return undefined;
	const result = Number(match[1]) / Number(match[2]);
	return Number.isFinite(result) && result > 0 ? result : undefined;
}

/** CMS transforms only: third-party URLs and non-raster media stay intact. */
export function getResponsiveImageProps(
	media: MediaSource | null | undefined,
	options: CmsImageOptions = {},
): ResponsiveImageAttributes & { src: string } {
	const src = resolveMediaUrl(media?.url) ?? "";
	const loading = options.priority ? "eager" : "lazy";
	const fetchPriority = options.priority ? "high" : "auto";
	const nativeWidth = positive(media?.width);
	const nativeHeight = positive(media?.height);
	const original = {
		src,
		width: nativeWidth,
		height: nativeHeight,
		loading,
		fetchPriority,
		decoding: "async",
	} as const;
	if (!media || !src) return original;
	const url = new URL(src, fallbackOrigin);
	const cmsOrigin = cmsBase().origin;
	// Vector media can have empty extension metadata in the Delivery API.
	const extension = (media.extension?.trim() || url.pathname.split(".").pop())
		?.toLowerCase()
		.replace(/^\./, "");
	if (
		!/^https?:$/.test(url.protocol) ||
		![cmsOrigin, fallbackOrigin].includes(url.origin) ||
		["svg", "gif"].includes(extension ?? "")
	)
		return original;
	const crop = options.cropAlias
		? media.crops?.find((c) => c.alias === options.cropAlias)
		: undefined;
	const existing = url.searchParams.get("cc")?.split(",").map(Number);
	const coords =
		crop?.coordinates ??
		(existing?.length === 4
			? { x1: existing[0], y1: existing[1], x2: existing[2], y2: existing[3] }
			: undefined);
	const values = coords ? [coords.x1, coords.y1, coords.x2, coords.y2] : [];
	const validCrop =
		values.length === 4 &&
		values.every(
			(value) =>
				typeof value === "number" &&
				Number.isFinite(value) &&
				value >= 0 &&
				value < 1,
		) &&
		Number(coords?.x1) + Number(coords?.x2) < 1 &&
		Number(coords?.y1) + Number(coords?.y2) < 1;
	const sourceWidth =
		nativeWidth && validCrop
			? Math.floor(nativeWidth * (1 - Number(coords?.x1) - Number(coords?.x2)))
			: nativeWidth;
	const sourceHeight =
		nativeHeight && validCrop
			? Math.floor(nativeHeight * (1 - Number(coords?.y1) - Number(coords?.y2)))
			: nativeHeight;
	const requestedWidth = positive(options.width) ?? positive(crop?.width);
	const requestedHeight = positive(options.height) ?? positive(crop?.height);
	const targetRatio =
		ratio(options.aspectRatio) ??
		(requestedWidth && requestedHeight
			? requestedWidth / requestedHeight
			: undefined);
	const maxWidth = Math.max(
		1,
		Math.min(
			2560,
			sourceWidth ?? 2560,
			targetRatio && sourceHeight
				? Math.floor(sourceHeight * targetRatio)
				: 2560,
		),
	);
	const width = Math.min(
		requestedWidth ??
			(requestedHeight && sourceWidth && sourceHeight
				? Math.round(
						requestedHeight * (targetRatio ?? sourceWidth / sourceHeight),
					)
				: 1200),
		maxWidth,
	);
	const heightFor = (w: number) =>
		targetRatio
			? Math.max(1, Math.round(w / targetRatio))
			: sourceWidth && sourceHeight
				? Math.max(1, Math.round((w * sourceHeight) / sourceWidth))
				: undefined;
	const buildUrl = (w: number) => {
		const candidate = new URL(url);
		for (const key of [
			"width",
			"height",
			"rmode",
			"rxy",
			"cc",
			"crop",
			"cropmode",
		])
			candidate.searchParams.delete(key);
		if (validCrop) candidate.searchParams.set("cc", values.join(","));
		candidate.searchParams.set("width", String(w));
		if (targetRatio) {
			candidate.searchParams.set("height", String(heightFor(w)));
			candidate.searchParams.set("rmode", "crop");
			const existingPoint = url.searchParams.get("rxy")?.split(",").map(Number);
			const fp =
				media.focalPoint ??
				(existingPoint?.length === 2
					? { left: existingPoint[0], top: existingPoint[1] }
					: undefined);
			if (
				!validCrop &&
				fp &&
				[fp.left, fp.top].every(
					(value) => Number.isFinite(value) && value >= 0 && value <= 1,
				)
			)
				candidate.searchParams.set("rxy", `${fp.left},${fp.top}`);
		}
		return candidate.origin === fallbackOrigin
			? `${candidate.pathname}${candidate.search}${candidate.hash}`
			: candidate.toString();
	};
	const widths = [
		...new Set([...candidateWidths.filter((w) => w <= maxWidth), maxWidth]),
	].sort((a, b) => a - b);
	return {
		...original,
		src: buildUrl(width),
		width,
		height: heightFor(width),
		srcSet: widths.map((w) => `${buildUrl(w)} ${w}w`).join(", "),
		sizes: options.sizes ?? "100vw",
	};
}

export function getMediaAlt(media: IApiMediaWithCropsModel) {
	return getAltText(media);
}

// Preserve exported entry points for existing consumers.
export function convertToCroppedFocalPointUrl(
	media: IApiMediaWithCropsModel,
	options?: CmsImageOptions,
) {
	if (!media?.url) return undefined;
	const crop = media.crops?.[0];
	return getResponsiveImageProps(media, {
		width: crop?.width,
		height: crop?.height,
		...options,
	}).src;
}
export function getFocalPointUrl(
	image: IApiMediaWithCropsModel | null | undefined,
	width: number,
	aspectRatio?: string | null,
) {
	return getResponsiveImageProps(image, { width, aspectRatio }).src;
}
export function calculateHeight(width: number, aspectRatio: string): number {
	const aspect = ratio(aspectRatio);
	if (!aspect)
		throw new Error(
			`Invalid aspect ratio "${aspectRatio}". Expected format like "16:9" or "16-9".`,
		);
	return width / aspect;
}
export interface UmbracoImageResult {
	url: string;
	width?: number;
	height?: number;
}
export function getUmbracoImageProps(
	image: IApiMediaWithCropsModel | null | undefined,
	cropAlias: string,
): UmbracoImageResult {
	const { src, width, height } = getResponsiveImageProps(image, { cropAlias });
	return { url: src, width, height };
}
export function getCropUrl(image: IApiMediaWithCropsModel, cropAlias: string) {
	return getUmbracoImageProps(image, cropAlias).url;
}
